<script lang="ts" setup>
import { type VNodeChild, computed, nextTick, onBeforeUnmount, ref, useSlots, watch } from 'vue';
import {
  IconFile,
  IconSearch,
  IconAlertTriangleFilled,
  IconAlertOctagonFilled,
  IconCheck,
} from '@/icons';
import { getLottieData } from '@/icons/lottie';
import { hasSlotContent } from '../../utils';
type Preset = 'loading' | 'success' | 'warning' | 'error' | 'search';
interface PresetProps {
  withBackground?: boolean;
  withIcon?: boolean;
  backgroundColor?: string;
  iconBackgroundColor?: string;
  iconColor?: string;
  rounded?: boolean;
}
interface Props extends PresetProps {
  preset?: Preset;
  size?: 'm' | 'l';
}
const { withIcon = true, rounded = true, withBackground = true, ...props } = defineProps<Props>();
defineSlots<{
  default?: () => VNodeChild;
  icon?: () => VNodeChild;
  text?: () => VNodeChild;
  content?: () => VNodeChild;
}>();
const presets: Record<Preset, PresetProps> = {
  loading: {},
  success: {
    backgroundColor: 'var(--ui2-state-success-shade, rgba(24, 165, 130, 0.08))',
    iconColor: 'var(--ui2-state-success, #007C5B)',
    iconBackgroundColor: 'var(--ui2-state-success-shade, rgba(24, 165, 130, 0.12))',
  },
  warning: {
    backgroundColor: 'rgba(228, 161, 75, 0.08)',
    iconColor: '#C27D0D',
    iconBackgroundColor: 'rgba(228, 161, 75, 0.16)',
  },
  error: {
    backgroundColor: 'rgba(230, 57, 70, 0.08)',
    iconColor: '#CC1731',
    iconBackgroundColor: 'rgba(230, 57, 70, 0.12)',
  },
  search: {},
};
const preset = computed(() => {
  return props.preset ? presets[props.preset] : undefined;
});
const computedWithBackground = computed(() => preset.value?.withBackground ?? withBackground);
const computedWithIcon = computed(() => preset.value?.withIcon ?? withIcon);
const computedBackgroundColor = computed(
  () => preset.value?.backgroundColor ?? props.backgroundColor,
);
const computedIconColor = computed(() => preset.value?.iconColor ?? props.iconColor);
const computedIconBackgroundColor = computed(
  () => preset.value?.iconBackgroundColor ?? props.iconBackgroundColor,
);
const computedRounded = computed(() => preset.value?.rounded ?? rounded);
const lottieColor = computed(() => computedIconColor.value || '#734e7c');
const iconPadding = computed(() => (props.preset === 'loading' ? '14px' : '18px'));
const lottieContainer = ref<HTMLElement | null>(null);
type LottieModule = {
  loadAnimation: (config: Record<string, unknown>) => {
    destroy: () => void;
  };
};
let lottieModule: LottieModule | null = null;
let lottieAnimation: {
  destroy: () => void;
} | null = null;
const slots = useSlots();
const hasIconSlot = computed(() => hasSlotContent(slots.icon));
const shouldRenderLottie = computed(() => props.preset === 'loading' && !hasIconSlot.value);
async function loadLottieModule() {
  if (lottieModule) {
    return lottieModule;
  }
  const module = await import('lottie-web');
  const normalizedModule = module as unknown as {
    default?: LottieModule;
  };
  lottieModule = normalizedModule.default ?? (module as unknown as LottieModule);
  return lottieModule;
}
function destroyLottie() {
  if (lottieAnimation) {
    lottieAnimation.destroy();
    lottieAnimation = null;
  }
  if (lottieContainer.value) {
    lottieContainer.value.innerHTML = '';
  }
}
async function renderLottie() {
  if (!shouldRenderLottie.value || !lottieContainer.value) {
    destroyLottie();
    return;
  }
  const data = await getLottieData('loader', lottieColor.value);
  if (!data) {
    destroyLottie();
    return;
  }
  destroyLottie();
  const { loadAnimation } = await loadLottieModule();
  lottieAnimation = loadAnimation({
    container: lottieContainer.value,
    renderer: 'svg',
    loop: true,
    autoplay: true,
    animationData: data as Record<string, unknown>,
  });
}
watch(
  [shouldRenderLottie, lottieColor],
  async ([shouldRender]) => {
    if (!shouldRender) {
      destroyLottie();
      return;
    }
    await nextTick();
    await renderLottie();
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  destroyLottie();
});
</script>

<template>
  <div
    :class="[
      $style.root,
      {
        [$style.rounded]: computedRounded,
        [$style.withoutBackground]: !computedWithBackground,
        [$style.sizeM]: props.size === 'm',
      },
    ]"
    :style="{
      '--background-color': computedBackgroundColor,
      '--icon-color': computedIconColor,
      '--icon-background-color': computedIconBackgroundColor,
      '--icon-padding': iconPadding,
    }"
    v-bind="$attrs"
    data-ui-component="UiEmptyState"
  >
    <div v-if="computedWithIcon" :class="$style.icon">
      <slot name="icon">
        <span v-if="props.preset === 'loading'" ref="lottieContainer" :class="$style.lottie" />
        <IconCheck v-else-if="props.preset === 'success'" />
        <IconAlertTriangleFilled v-else-if="props.preset === 'warning'" />
        <IconAlertOctagonFilled v-else-if="props.preset === 'error'" />
        <IconSearch v-else-if="props.preset === 'search'" />
        <IconFile v-else />
      </slot>
    </div>
    <div
      v-if="hasSlotContent($slots.default) || hasSlotContent($slots.text)"
      :class="$style.textContainer"
    >
      <div v-if="hasSlotContent($slots.default)" :class="$style.title">
        <slot />
      </div>
      <div v-if="hasSlotContent($slots.text)" :class="$style.text">
        <slot name="text" />
      </div>
    </div>
    <div v-if="hasSlotContent($slots.content)" :class="$style.content">
      <slot name="content" />
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  --background-color: #f5f5f5;
  --icon-color: #734e7c;
  --icon-background-color: rgba(160, 120, 169, 0.12);

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 20px;
  font-family: typography.$font-family-primary;
  color: #010000;
  text-align: center;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  background-color: var(--background-color);
  gap: 20px;
}

.rounded {
  border-radius: 20px;
}

.withoutBackground {
  background: none;
}

.icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  padding: var(--icon-padding, 18px);
  box-sizing: border-box;
  color: var(--icon-color);
  background-color: var(--icon-background-color);

  > svg {
    width: 100%;
    height: 100%;
  }
}

.textContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.title {
  font-size: 22px;
  line-height: 24px;
  font-weight: 700;
  font-style: normal;
  letter-spacing: -0.66px;
  max-width: 360px;
}

.text {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  max-width: 360px;
  color: #655e60;
}

.content {
  display: flex;
  justify-content: center;
  width: 100%;
}

.lottie {
  width: 100%;
  height: 100%;
}

.sizeM {
  gap: 20px;

  .textContainer {
    gap: 4px;
  }

  .title {
    font-size: 16px;
    line-height: 20px;
  }
}
</style>
